// OnboardingGate.jsx -- renders the current onboarding step, or the app (spec §4 step 7). Pre-consent
// screens sit inside an ErrorBoundary whose fallback reports through the anonymous crash beacon.
import { useCallback, useEffect } from 'react';
import ErrorBoundary from '../../components/ErrorBoundary';
import AuthScreen from '../../pages/AuthScreen';
import { captureError } from '../../lib/analytics';
import { isCapacitorNative } from '../../lib/platform';
import { useOnboardingGate } from './useOnboardingGate';
import { sendCrash } from './crashBeacon';
import WelcomeScreen from './WelcomeScreen';
import AgeScreen from './AgeScreen';
import TermsScreen from './TermsScreen';
import BlockedScreen from './BlockedScreen';
import GateLoadingScreen from './GateLoadingScreen';
import GateCrashScreen from './GateCrashScreen';

// onAppVisible(bool): whether the app (children) is showing with analytics allowed. App gates screen
// tracking on it, so the landing screen_viewed fires after this gate has started analytics, not into an
// uninitialized or paused SDK (#1446 F3: the app can show while a held account read keeps analytics off).
export default function OnboardingGate({ auth, children, onAppVisible }) {
  const { step, stalled, versions, allowed, handlers } = useOnboardingGate({ user: auth.user, authResolved: auth.authResolved, showAuth: auth.showAuth });
  // Declared after the hook, so it runs after the hook's analytics start/resume in the same commit.
  useEffect(() => { if (onAppVisible) onAppVisible(step === 'app' && allowed); }, [step, allowed, onAppVisible]);
  const report = useCallback((err, s) => {
    if (allowed) captureError(err, { fn: 'OnboardingGate', stage: s });
    else sendCrash(err, { step: s, isNative: isCapacitorNative });
  }, [allowed]);

  let screen;
  switch (step) {
    case 'blocked': screen = <BlockedScreen signedIn={Boolean(auth.user)} onSignOut={handlers.onSignOut} />; break;
    case 'loading': screen = <GateLoadingScreen error={false} stalled={stalled} onRetry={handlers.onRetry} onReload={handlers.onReload} />; break;
    case 'account_error': screen = <GateLoadingScreen error stalled={false} onRetry={handlers.onRetry} onReload={handlers.onReload} />; break;
    case 'welcome': screen = <WelcomeScreen onContinue={handlers.onWelcomeContinue} />; break;
    case 'age': screen = <AgeScreen onConfirm={handlers.onAgeConfirm} />; break;
    case 'terms': screen = <TermsScreen key={`${versions.terms}|${versions.privacy}`} onAgree={handlers.onAgree} onOpenLegal={handlers.onOpenLegal} />; break;
    case 'signin': screen = <AuthScreen onAuth={auth.closeAuth} onSignIn={auth.signInWithGoogle} onSignInApple={auth.signInWithApple} />; break;
    default: return children;
  }
  return (
    <ErrorBoundary name="onboarding" resetKey={step} context={{ step }} fallback={({ error, reset }) => <GateCrashScreen error={error} step={step} onRetry={reset} report={report} />}>
      {screen}
    </ErrorBoundary>
  );
}
